<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  filterTypes: {
    type: Array,
    required: true,
  },
  isSegmentView: {
    type: Boolean,
    default: false,
  },
  segmentName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'applyFilter',
  'updateSegment',
  'clearFilters',
  'close',
]);

const filters = defineModel({
  type: Array,
  default: [],
});

const { t } = useI18n();

const VIEW = {
  MAIN: 'main',
  LABELS: 'labels',
  CITY: 'city',
  COUNTRY: 'country_code',
  CREATED_AT: 'created_at',
  LAST_ACTIVITY_AT: 'last_activity_at',
  BLOCKED: 'blocked',
  ADVANCED: 'advanced',
};

const MANAGED_KEYS = [
  VIEW.LABELS,
  VIEW.CITY,
  VIEW.COUNTRY,
  VIEW.CREATED_AT,
  VIEW.LAST_ACTIVITY_AT,
  VIEW.BLOCKED,
];

const DEFAULT_OPERATORS = {
  [VIEW.LABELS]: 'equal_to',
  [VIEW.CITY]: 'contains',
  [VIEW.COUNTRY]: 'equal_to',
  [VIEW.CREATED_AT]: 'days_before',
  [VIEW.LAST_ACTIVITY_AT]: 'days_before',
  [VIEW.BLOCKED]: 'equal_to',
};

const DATE_PRESETS = [
  { labelKey: 'OLDER_THAN_7_DAYS', value: '7' },
  { labelKey: 'OLDER_THAN_30_DAYS', value: '30' },
  { labelKey: 'OLDER_THAN_90_DAYS', value: '90' },
  { labelKey: 'OLDER_THAN_365_DAYS', value: '365' },
];

const activeView = ref(VIEW.MAIN);
const searchQuery = ref('');
const cityValue = ref('');
const segmentNameLocal = ref(props.segmentName);

watch(
  () => props.segmentName,
  value => {
    segmentNameLocal.value = value;
  }
);

const filterByKey = computed(() => {
  return props.filterTypes.reduce((acc, filterType) => {
    acc[filterType.attributeKey] = filterType;
    return acc;
  }, {});
});

const getFilterType = key => filterByKey.value[key] || {};

const hasValue = filter => {
  const value = filter?.values;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Boolean(value.id);
  return value !== undefined && value !== null && value !== '';
};

const meaningfulFilters = computed(() => filters.value.filter(hasValue));

const findFilter = key =>
  meaningfulFilters.value.find(filter => filter.attributeKey === key);

const getOptionValue = key => {
  const filter = findFilter(key);
  const value = filter?.values;
  if (Array.isArray(value)) return value[0];
  return value;
};

const optionById = (options, value) => {
  if (!value) return null;
  if (typeof value === 'object' && value.id) return value;

  const id = Array.isArray(value) ? value[0] : value;
  return (
    options.find(option => String(option.id).toLowerCase() === String(id)) ||
    null
  );
};

const labelsOptions = computed(() => getFilterType(VIEW.LABELS).options || []);
const countryOptions = computed(
  () => getFilterType(VIEW.COUNTRY).options || []
);
const blockedOptions = computed(
  () => getFilterType(VIEW.BLOCKED).options || []
);

const selectedLabels = computed(() => {
  const values = findFilter(VIEW.LABELS)?.values || [];
  return values.map(value => optionById(labelsOptions.value, value) || value);
});

const selectedCountry = computed(() =>
  optionById(countryOptions.value, getOptionValue(VIEW.COUNTRY))
);

const selectedBlocked = computed(() =>
  optionById(blockedOptions.value, getOptionValue(VIEW.BLOCKED))
);

const selectedDatePreset = key => {
  const value = getOptionValue(key);
  return DATE_PRESETS.find(preset => preset.value === String(value));
};

const selectedCreatedAtPreset = computed(() =>
  selectedDatePreset(VIEW.CREATED_AT)
);

const selectedLastActivityPreset = computed(() =>
  selectedDatePreset(VIEW.LAST_ACTIVITY_AT)
);

watch(
  () => findFilter(VIEW.CITY)?.values,
  value => {
    cityValue.value = value || '';
  },
  { immediate: true }
);

const cleanFilters = nextFilters => nextFilters.filter(hasValue);

const removeFilter = key => {
  filters.value = filters.value.filter(filter => filter.attributeKey !== key);
};

const upsertFilter = (key, values, operator = DEFAULT_OPERATORS[key]) => {
  const filterType = getFilterType(key);
  const existing = filters.value.find(filter => filter.attributeKey === key);

  if (!hasValue({ values })) {
    removeFilter(key);
    return;
  }

  const nextFilter = {
    attributeKey: key,
    attributeModel: filterType.attributeModel || 'standard',
    customAttributeType: filterType.customAttributeType,
    filterOperator: operator,
    queryOperator: existing?.queryOperator || 'and',
    values,
  };

  const nextFilters = filters.value.filter(
    filter => filter.attributeKey !== key
  );
  filters.value = cleanFilters([...nextFilters, nextFilter]);
};

const isLabelSelected = option =>
  selectedLabels.value.some(label => label.id === option.id);

const toggleLabel = option => {
  const nextLabels = isLabelSelected(option)
    ? selectedLabels.value.filter(label => label.id !== option.id)
    : [...selectedLabels.value, { id: option.id, name: option.name }];

  upsertFilter(VIEW.LABELS, nextLabels);
};

const filteredOptions = options => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return options;
  return options.filter(option => option.name.toLowerCase().includes(query));
};

const filteredLabels = computed(() => filteredOptions(labelsOptions.value));
const filteredCountries = computed(() => filteredOptions(countryOptions.value));

const setCity = () => {
  upsertFilter(VIEW.CITY, cityValue.value.trim());
  activeView.value = VIEW.MAIN;
};

const setSingleOption = (key, option) => {
  upsertFilter(key, { id: option.id, name: option.name });
  activeView.value = VIEW.MAIN;
};

const setDatePreset = (key, preset) => {
  upsertFilter(key, preset.value);
  activeView.value = VIEW.MAIN;
};

const resetSearchAndOpen = view => {
  searchQuery.value = '';
  activeView.value = view;
};

const formatDatePreset = preset => {
  if (!preset) return '';
  return t(`CONTACTS_LAYOUT.MOBILE_FILTER.DATE_PRESETS.${preset.labelKey}`);
};

const summaries = computed(() => {
  return [
    {
      key: VIEW.LABELS,
      label: t('CONTACTS_FILTER.ATTRIBUTES.LABELS'),
      value: selectedLabels.value.map(label => label.name).join(', '),
    },
    {
      key: VIEW.CITY,
      label: t('CONTACTS_LAYOUT.FILTER.CITY'),
      value: findFilter(VIEW.CITY)?.values || '',
    },
    {
      key: VIEW.COUNTRY,
      label: t('CONTACTS_LAYOUT.FILTER.COUNTRY'),
      value: selectedCountry.value?.name || '',
    },
    {
      key: VIEW.CREATED_AT,
      label: t('CONTACTS_LAYOUT.FILTER.CREATED_AT'),
      value: formatDatePreset(selectedCreatedAtPreset.value),
    },
    {
      key: VIEW.LAST_ACTIVITY_AT,
      label: t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY'),
      value: formatDatePreset(selectedLastActivityPreset.value),
    },
    {
      key: VIEW.BLOCKED,
      label: t('CONTACTS_LAYOUT.FILTER.BLOCKED'),
      value: selectedBlocked.value?.name || '',
    },
  ].filter(item => item.value);
});

const unknownFilters = computed(() =>
  meaningfulFilters.value.filter(
    filter => !MANAGED_KEYS.includes(filter.attributeKey)
  )
);

const categories = computed(() => [
  {
    key: VIEW.LABELS,
    icon: 'i-lucide-tags',
    label: t('CONTACTS_FILTER.ATTRIBUTES.LABELS'),
    summary: summaries.value.find(item => item.key === VIEW.LABELS)?.value,
  },
  {
    key: VIEW.CITY,
    icon: 'i-lucide-building-2',
    label: t('CONTACTS_LAYOUT.FILTER.CITY'),
    summary: summaries.value.find(item => item.key === VIEW.CITY)?.value,
  },
  {
    key: VIEW.COUNTRY,
    icon: 'i-lucide-map-pin',
    label: t('CONTACTS_LAYOUT.FILTER.COUNTRY'),
    summary: summaries.value.find(item => item.key === VIEW.COUNTRY)?.value,
  },
  {
    key: VIEW.CREATED_AT,
    icon: 'i-lucide-calendar-plus',
    label: t('CONTACTS_LAYOUT.FILTER.CREATED_AT'),
    summary: summaries.value.find(item => item.key === VIEW.CREATED_AT)?.value,
  },
  {
    key: VIEW.LAST_ACTIVITY_AT,
    icon: 'i-lucide-activity',
    label: t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY'),
    summary: summaries.value.find(item => item.key === VIEW.LAST_ACTIVITY_AT)
      ?.value,
  },
  {
    key: VIEW.BLOCKED,
    icon: 'i-lucide-ban',
    label: t('CONTACTS_LAYOUT.FILTER.BLOCKED'),
    summary: summaries.value.find(item => item.key === VIEW.BLOCKED)?.value,
  },
  {
    key: VIEW.ADVANCED,
    icon: 'i-lucide-sliders-horizontal',
    label: t('CONTACTS_LAYOUT.MOBILE_FILTER.ADVANCED_FILTERS'),
    summary: unknownFilters.value.length
      ? t('CONTACTS_LAYOUT.MOBILE_FILTER.ACTIVE_COUNT', {
          count: unknownFilters.value.length,
        })
      : '',
  },
]);

const clearAll = () => {
  filters.value = [];
  emit('clearFilters');
};

const applyFilters = () => {
  filters.value = cleanFilters(filters.value);

  if (!filters.value.length) {
    emit('clearFilters');
    emit('close');
    return;
  }

  if (props.isSegmentView) {
    emit('updateSegment', filters.value, segmentNameLocal.value);
  } else {
    emit('applyFilter', filters.value);
  }
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-col md:h-[680px]">
    <main class="min-h-0 flex-1 overflow-y-auto pb-4">
      <template v-if="activeView === VIEW.MAIN">
        <div v-if="isSegmentView" class="mb-5">
          <Input
            v-model="segmentNameLocal"
            :label="t('CONTACTS_LAYOUT.FILTER.SEGMENT.LABEL')"
            :placeholder="t('CONTACTS_LAYOUT.FILTER.SEGMENT.INPUT_PLACEHOLDER')"
          />
        </div>

        <section v-if="summaries.length || unknownFilters.length" class="mb-6">
          <h4 class="mb-3 text-sm font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.MOBILE_FILTER.ACTIVE_FILTERS') }}
          </h4>
          <div
            class="flex flex-col overflow-hidden rounded-xl border border-n-weak"
          >
            <div
              v-for="summary in summaries"
              :key="summary.key"
              class="flex items-center gap-3 border-b border-n-weak px-3 py-3 last:border-b-0"
            >
              <div class="min-w-0 flex-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ summary.label }}
                </p>
                <p class="mb-0 truncate text-sm text-n-slate-11">
                  {{ summary.value }}
                </p>
              </div>
              <Button
                icon="i-lucide-x"
                color="slate"
                variant="ghost"
                size="sm"
                type="button"
                class="!rounded-full"
                @click="removeFilter(summary.key)"
              />
            </div>
            <div
              v-if="unknownFilters.length"
              class="flex items-center gap-3 px-3 py-3"
            >
              <div class="min-w-0 flex-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ t('CONTACTS_LAYOUT.MOBILE_FILTER.ADVANCED_ACTIVE') }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{
                    t('CONTACTS_LAYOUT.MOBILE_FILTER.ACTIVE_COUNT', {
                      count: unknownFilters.length,
                    })
                  }}
                </p>
              </div>
              <Icon
                icon="i-lucide-chevron-right"
                class="size-4 text-n-slate-10"
              />
            </div>
          </div>
        </section>

        <section>
          <h4 class="mb-3 text-sm font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.MOBILE_FILTER.FILTER_BY') }}
          </h4>
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="category in categories"
              :key="category.key"
              type="button"
              class="flex w-full items-center gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="resetSearchAndOpen(category.key)"
            >
              <Icon
                :icon="category.icon"
                class="size-4 flex-shrink-0 text-n-slate-10"
              />
              <div class="min-w-0 flex-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ category.label }}
                </p>
                <p
                  v-if="category.summary"
                  class="mb-0 truncate text-sm text-n-slate-11"
                >
                  {{ category.summary }}
                </p>
              </div>
              <Icon
                icon="i-lucide-chevron-right"
                class="size-4 text-n-slate-10"
              />
            </button>
          </div>
        </section>
      </template>

      <template v-else>
        <button
          type="button"
          class="mb-5 flex items-center gap-2 text-sm font-medium text-n-slate-11"
          @click="activeView = VIEW.MAIN"
        >
          <Icon icon="i-lucide-chevron-left" class="size-4" />
          {{ t('CONTACTS_LAYOUT.MOBILE_FILTER.BACK') }}
        </button>

        <section v-if="activeView === VIEW.LABELS">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_FILTER.ATTRIBUTES.LABELS') }}
          </h4>
          <input
            v-model="searchQuery"
            type="search"
            class="reset-base mb-3 h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('CONTACTS_LAYOUT.MOBILE_FILTER.SEARCH')"
          />
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="label in filteredLabels"
              :key="label.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="toggleLabel(label)"
            >
              <span class="text-sm text-n-slate-12">{{ label.name }}</span>
              <Icon
                v-if="isLabelSelected(label)"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <section v-else-if="activeView === VIEW.CITY">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.FILTER.CITY') }}
          </h4>
          <input
            v-model="cityValue"
            type="text"
            class="reset-base h-11 w-full appearance-none rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('CONTACTS_LAYOUT.MOBILE_FILTER.CITY_PLACEHOLDER')"
            @keyup.enter="setCity"
          />
          <Button
            class="mt-4 w-full"
            :label="t('CONTACTS_LAYOUT.MOBILE_FILTER.SAVE_VALUE')"
            @click="setCity"
          />
        </section>

        <section v-else-if="activeView === VIEW.COUNTRY">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.FILTER.COUNTRY') }}
          </h4>
          <input
            v-model="searchQuery"
            type="search"
            class="reset-base mb-3 h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('CONTACTS_LAYOUT.MOBILE_FILTER.SEARCH')"
          />
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="country in filteredCountries"
              :key="country.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="setSingleOption(VIEW.COUNTRY, country)"
            >
              <span class="text-sm text-n-slate-12">{{ country.name }}</span>
              <Icon
                v-if="selectedCountry?.id === country.id"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <section
          v-else-if="
            activeView === VIEW.CREATED_AT ||
            activeView === VIEW.LAST_ACTIVITY_AT
          "
        >
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{
              activeView === VIEW.CREATED_AT
                ? t('CONTACTS_LAYOUT.FILTER.CREATED_AT')
                : t('CONTACTS_LAYOUT.FILTER.LAST_ACTIVITY')
            }}
          </h4>
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="preset in DATE_PRESETS"
              :key="preset.value"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="setDatePreset(activeView, preset)"
            >
              <span class="text-sm text-n-slate-12">
                {{ formatDatePreset(preset) }}
              </span>
              <Icon
                v-if="selectedDatePreset(activeView)?.value === preset.value"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <section v-else-if="activeView === VIEW.BLOCKED">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.FILTER.BLOCKED') }}
          </h4>
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="option in blockedOptions"
              :key="option.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="setSingleOption(VIEW.BLOCKED, option)"
            >
              <span class="text-sm text-n-slate-12">{{ option.name }}</span>
              <Icon
                v-if="selectedBlocked?.id === option.id"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <section v-else-if="activeView === VIEW.ADVANCED">
          <h4 class="mb-2 text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.MOBILE_FILTER.ADVANCED_FILTERS') }}
          </h4>
          <p class="mb-4 text-sm leading-6 text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.MOBILE_FILTER.ADVANCED_DESCRIPTION') }}
          </p>
          <div
            v-if="unknownFilters.length"
            class="overflow-hidden rounded-xl border border-n-weak"
          >
            <div
              v-for="filter in unknownFilters"
              :key="`${filter.attributeKey}-${filter.filterOperator}`"
              class="flex items-center gap-3 border-b border-n-weak px-3 py-4 last:border-b-0"
            >
              <div class="min-w-0 flex-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ filter.attributeKey }}
                </p>
                <p class="mb-0 truncate text-sm text-n-slate-11">
                  {{ filter.filterOperator }}
                </p>
              </div>
              <Button
                icon="i-lucide-x"
                color="slate"
                variant="ghost"
                size="sm"
                type="button"
                class="!rounded-full"
                @click="removeFilter(filter.attributeKey)"
              />
            </div>
          </div>
        </section>
      </template>
    </main>

    <footer
      class="-mx-4 md:mx-0 border-t border-n-weak bg-n-solid-2 px-4 md:px-0 pt-4"
    >
      <div class="flex gap-3">
        <Button
          class="w-full"
          color="slate"
          variant="faded"
          :label="t('CONTACTS_LAYOUT.MOBILE_FILTER.RESET')"
          @click="clearAll"
        />
        <Button
          class="w-full"
          :label="
            isSegmentView
              ? t('CONTACTS_LAYOUT.FILTER.BUTTONS.UPDATE_SEGMENT')
              : t('CONTACTS_LAYOUT.MOBILE_FILTER.SHOW_CONTACTS')
          "
          :disabled="isSegmentView && !segmentNameLocal"
          @click="applyFilters"
        />
      </div>
    </footer>
  </div>
</template>

<style scoped>
@media (max-width: 767px) {
  footer {
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
  }
}
@media (min-width: 768px) {
  footer {
    padding-bottom: 1.5rem;
  }
}
</style>
